import { useCallback, useMemo, useState } from 'react';

import { usePericlesClient } from '../core/PericlesProvider';

import { buildEntityPathSegment } from './entityPath';

/**
 * Parameters for creating a new entity record.
 */
export interface UseCreateEntityParams {
  entity: string;
}

/**
 * Result returned by the create hook, including submission state.
 */
export interface UseCreateEntityResult<TInput, TOutput> {
  create: (values: TInput) => Promise<TOutput | undefined>;
  isSubmitting: boolean;
  isError: boolean;
  error: unknown;
  data: TOutput | undefined;
}

/**
 * Issues a POST request to create an entity using the Pericles client configuration.
 */
export function useCreateEntity<TInput = unknown, TOutput = TInput>(
  params: UseCreateEntityParams
): UseCreateEntityResult<TInput, TOutput> {
  const { baseUrl, fetcher } = usePericlesClient();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(undefined);
  const [data, setData] = useState<TOutput | undefined>(undefined);

  const base = useMemo(() => baseUrl.replace(/\/$/, ''), [baseUrl]);

  const url = useMemo(
    () => `${base}/entities/${buildEntityPathSegment(params.entity)}`,
    [base, params.entity]
  );

  const create = useCallback(
    async (values: TInput) => {
      setIsSubmitting(true);
      setIsError(false);
      setError(undefined);

      try {
        const response = await fetcher(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        let json: TOutput | undefined;

        try {
          json = (await response.json()) as TOutput;
        } catch {
          json = undefined;
        }

        setData(json);
        return json;
      } catch (err) {
        setIsError(true);
        setError(err);
        setData(undefined);
        return undefined;
      } finally {
        setIsSubmitting(false);
      }
    },
    [fetcher, url]
  );

  return {
    create,
    isSubmitting,
    isError,
    error,
    data,
  };
}
